import React, { forwardRef } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { Spinner } from '../Spinner';
import styles from './Button2.module.css';

/**
 * Button (2.0) — the single shared source for the 2.0 button look, so consumers
 * never re-declare button styling. Reimplemented in Athena's idiom (JSX + CSS
 * Module + 2.0 `--button-*` tokens); self-contained (renders its own <button>,
 * or an <a> when `href` is set). Preview-only for now (globals.css isn't
 * imported into the app); the live 1.0 components/ui/Button is untouched.
 *
 * Lives in Button2/ because the 1.0 Button owns the `Button/` folder (mirrors
 * the PopoverMenu-vs-Popover precedent). Variant names follow shadcn.
 *
 *   variant: 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link'
 *   size:    'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg'
 */

const VARIANT_CLASS = {
  default: styles.vDefault,
  outline: styles.vOutline,
  secondary: styles.vSecondary,
  ghost: styles.vGhost,
  destructive: styles.vDestructive,
  link: styles.vLink,
};

const SIZE_CLASS = {
  default: styles.sDefault,
  xs: styles.sXs,
  sm: styles.sSm,
  lg: styles.sLg,
  icon: styles.sIcon,
  'icon-xs': styles.sIconXs,
  'icon-sm': styles.sIconSm,
  'icon-lg': styles.sIconLg,
};

/** Compose the button class string — mirrors shadcn's `buttonVariants` cva
 *  helper. Usable standalone (e.g. on a plain <a>) to make it look like a button. */
export function buttonVariants({ variant = 'default', size = 'default', className = '' } = {}) {
  return [styles.btn, VARIANT_CLASS[variant], SIZE_CLASS[size], className]
    .filter(Boolean)
    .join(' ');
}

const Button = forwardRef(
  (
    {
      variant = 'default',
      size = 'default',
      className = '',
      loading = false,
      disabled = false,
      startIcon,
      endIcon,
      href,
      type = 'button',
      children,
      ...props
    },
    ref
  ) => {
    const cls = buttonVariants({ variant, size, className });
    const isDisabled = disabled || loading;
    const content = (
      <>
        {loading && <Spinner />}
        {!loading && startIcon && <span className={styles.icon} aria-hidden="true">{startIcon}</span>}
        {children}
        {endIcon && <span className={styles.icon} aria-hidden="true">{endIcon}</span>}
      </>
    );

    if (href) {
      return (
        <a
          ref={ref}
          href={isDisabled ? undefined : href}
          className={cls}
          aria-disabled={isDisabled || undefined}
          role="button"
          {...props}
        >
          {content}
        </a>
      );
    }

    return (
      <button ref={ref} type={type} className={cls} disabled={isDisabled} aria-busy={loading || undefined} {...props}>
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

// ButtonGroup — visually joins adjacent buttons (collapsed borders).
const ButtonGroup = ({ className = '', children, ...props }) => (
  <div role="group" className={`${styles.group} ${className}`} {...props}>
    {children}
  </div>
);

Button.propTypes = {
  variant: PropTypes.string,
  size: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  href: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.node,
};
ButtonGroup.propTypes = { className: PropTypes.string, children: PropTypes.node };

export default Button;
export { Button, ButtonGroup };
